// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { LOCALE_EN, Resources } from '@shared/localizations/Resources';
import { TvChartSupportedResolutionArray } from '../TradingViewPrimitives/TvEnums';
class _TvHistoryAggregationsHelper {
    constructor() {
        this._keySupportedResolutions = 'TvTradingPlatform.SupportedResolutions';
        this._keyCustomAggregationsAllowed = 'TvTradingPlatform.CustomAggregationsAllowed';
        this._isHasCustomAggregations = false;
        this._isCustomAggregationsAllowed = false;
        this.ticksResolution = [];
        this.secondResolutions = [];
        this.secondMultipliers = [];
        this.dayResolutions = [];
        this.dayMultipliers = [];
        this.weekResolutions = [];
        this.weekMultipliers = [];
        this.monthResolutions = [];
        this.monthMultipliers = [];
        this.intradayResolutions = [];
        this.intradayMultipliers = [];
    }
    getSupportedResolutions() {
        if (!this._isHasCustomAggregations) {
            return TvChartSupportedResolutionArray;
        }
        return [
            ...this.ticksResolution,
            ...this.secondResolutions,
            ...this.intradayResolutions,
            ...this.dayResolutions,
            ...this.weekResolutions,
            ...this.monthResolutions
        ];
    }
    getSecondsMultipliers() {
        if (!this._isHasCustomAggregations) {
            return [];
        }
        return this.secondMultipliers;
    }
    getIntradayMultipliers() {
        if (!this._isHasCustomAggregations) {
            return ['1', '2', '3', '5', '15', '30', '60', '240'];
        }
        return this.intradayMultipliers;
    }
    getDailyMultipliers() {
        if (!this._isHasCustomAggregations) {
            return ['1'];
        }
        return this.dayMultipliers;
    }
    getWeeklyMultipliers() {
        if (!this._isHasCustomAggregations) {
            return ['1'];
        }
        return this.weekMultipliers;
    }
    getMonthlyMultipliers() {
        if (!this._isHasCustomAggregations) {
            return ['1', '12'];
        }
        return this.monthMultipliers;
    }
    hasWeeklyAndMonthly() {
        return this.getWeeklyMultipliers().length > 0 && this.getMonthlyMultipliers().length > 0;
    }
    hasDaily() {
        return this.getDailyMultipliers().length > 0;
    }
    hasIntraday() {
        return this.getIntradayMultipliers().length > 0;
    }
    hasSeconds() {
        return this.getSecondsMultipliers().length > 0;
    }
    hasTicks() {
        return this.ticksResolution.length > 0;
    }
    isCustomAggregationsAllowed() {
        return this._isCustomAggregationsAllowed;
    }
    parseKeys() {
        this.clearResolutions();
        this.parseCustomAggregations();
        this.parseIsCustomAggregationsAllowed();
    }
    parseCustomAggregations() {
        this._isHasCustomAggregations =
            Resources.IsResourcePresentEN(this._keySupportedResolutions) &&
                !Resources.isHidden(this._keySupportedResolutions);
        if (!this._isHasCustomAggregations) {
            return;
        }
        try {
            const json = Resources.getResourceLang(this._keySupportedResolutions, LOCALE_EN);
            const data = JSON.parse(json);
            if (data.length === 0) {
                this._isHasCustomAggregations = true;
            }
            for (const agg of data) {
                if (agg.endsWith('T')) {
                    this.ticksResolution.push(agg);
                }
                else if (agg.endsWith('S')) {
                    this.secondResolutions.push(agg);
                    this.secondMultipliers.push(this.getMultiplier(agg));
                }
                else if (agg.endsWith('D')) {
                    this.dayResolutions.push(agg);
                    this.dayMultipliers.push(this.getMultiplier(agg));
                }
                else if (agg.endsWith('W')) {
                    this.weekResolutions.push(agg);
                    this.weekMultipliers.push(this.getMultiplier(agg));
                }
                else if (agg.endsWith('M')) {
                    this.monthResolutions.push(agg);
                    this.monthMultipliers.push(this.getMultiplier(agg));
                }
                else {
                    this.intradayResolutions.push(agg);
                    this.intradayMultipliers.push(this.getMultiplier(agg));
                }
            }
        }
        catch (e) {
            this._isHasCustomAggregations = false;
            console.error('Error parsing custom TV aggregations');
        }
    }
    parseIsCustomAggregationsAllowed() {
        try {
            this._isCustomAggregationsAllowed =
                Resources.getResourceLang(this._keyCustomAggregationsAllowed, LOCALE_EN) === 'true';
        }
        catch (e) {
            this._isCustomAggregationsAllowed = false;
            console.error('Error parsing TV allow custom aggregations');
        }
    }
    getMultiplier(resolution) {
        return resolution.slice(0, -1);
    }
    clearResolutions() {
        this.ticksResolution.length = 0;
        this.secondResolutions.length = 0;
        this.secondMultipliers.length = 0;
        this.dayResolutions.length = 0;
        this.dayMultipliers.length = 0;
        this.weekResolutions.length = 0;
        this.weekMultipliers.length = 0;
        this.monthResolutions.length = 0;
        this.monthMultipliers.length = 0;
        this.intradayResolutions.length = 0;
        this.intradayMultipliers.length = 0;
    }
}
export const TvHistoryAggregationsHelper = new _TvHistoryAggregationsHelper();
